/**
 * Grouped dashboard table — group rows (Kunde or ProduktType), the instance
 * rows inside an expanded group, and the selection checkboxes.
 *
 * A ready instance is notified straight from its own status cell; ticking
 * several instances sends them as one batch from the selection bar.
 * Every checkbox and notify button is permission-gated — without
 * `notification.send` it stays visible but disabled and carries the German
 * reason as its title.
 */

import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Chip, ChipList } from '../../components/Chip';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { Icon } from '../../components/Icon';
import { DASH, countLabel, fmtDate, fmtVersion } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type { OverviewRow } from '../../lib/types';
import {
  auditLogLink,
  bulkIds,
  groupSelectState,
  isSelectable,
  type GateMap,
  type GroupMode,
  type OverviewGroup,
  type SelectState,
} from './dashboardGroups';
import { orFallback, type NotifyGate } from './dashboardModel';
import { ComponentsCell, CvesCell, KomponentenDetail } from './ProductCells';
import type { ExploitabilityStore } from './useExploitability';

const NO_PERMISSION_TITLE = missingPermissionTitle('notification.send');

/** Rows per page; below that the pagination bars are just noise. */
const PAGE_SIZE = 10;

function pageSizeFor(count: number): number | undefined {
  return count > PAGE_SIZE ? PAGE_SIZE : undefined;
}

/** Rows toggle on click; interactive cells swallow it. */
const swallow = (event: MouseEvent): void => event.stopPropagation();

/* ------------------------------------------------------------------------- *
 * Checkbox
 * ------------------------------------------------------------------------- */

interface SelectBoxProps {
  state: SelectState;
  disabled: boolean;
  /** Accessible name. */
  label: string;
  title?: string;
  onChange: (checked: boolean) => void;
}

/** Tri-state checkbox; `indeterminate` is a DOM property, not an attribute. */
function SelectBox({ state, disabled, label, title, onChange }: SelectBoxProps): ReactNode {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = state === 'some';
  }, [state]);
  return (
    <label className="dash-select" onClick={swallow} title={title}>
      <input
        ref={ref}
        type="checkbox"
        checked={state === 'all'}
        disabled={disabled}
        aria-label={label}
        onChange={() => onChange(state !== 'all')}
      />
    </label>
  );
}

/* ------------------------------------------------------------------------- *
 * Shared props
 * ------------------------------------------------------------------------- */

export interface SelectionProps {
  gates: GateMap;
  selected: ReadonlySet<number>;
  /** `notification.send`. */
  canNotify: boolean;
  /** `triage.read` — only then is a link to the triage queue useful. */
  canReadTriage: boolean;
  /** Add (`true`) or remove (`false`) instance ids from the selection. */
  onSelect: (ids: readonly number[], checked: boolean) => void;
  /** Notify exactly this one instance. */
  onNotifyOne: (row: OverviewRow) => void;
}

/* ------------------------------------------------------------------------- *
 * Status — per instance and summarised per group
 * ------------------------------------------------------------------------- */

function InstanceStatus({
  row,
  gate,
  canNotify,
  canReadTriage,
  onNotifyOne,
}: {
  row: OverviewRow;
  gate: NotifyGate | undefined;
  canNotify: boolean;
  canReadTriage: boolean;
  onNotifyOne: (row: OverviewRow) => void;
}): ReactNode {
  if (gate === undefined || gate.kind === 'clean') {
    return (
      <span className="muted" title="Ohne KEV-Treffer ist nichts zu melden">
        {DASH}
      </span>
    );
  }
  if (gate.kind === 'notified') {
    return (
      <div className="dash-status">
        <Chip tone="good">benachrichtigt</Chip>
        <span className="prod">{fmtDate(gate.versendetAm)}</span>
        {/* The Audit-Log is guarded by triage.read, like the triage links. */}
        {canReadTriage ? (
          <Link to={auditLogLink(row)} onClick={swallow}>
            Im Audit-Log ansehen
          </Link>
        ) : null}
      </div>
    );
  }
  if (gate.kind === 'ready') {
    return gate.kundeResolvable ? (
      <button
        type="button"
        className="primary small"
        onClick={(event) => {
          swallow(event);
          onNotifyOne(row);
        }}
        disabled={!canNotify}
        title={
          canNotify
            ? 'Bewertung „Betroffen“ liegt vor. Anschreiben für diesen Kunden erzeugen.'
            : NO_PERMISSION_TITLE
        }
      >
        Benachrichtigen
      </button>
    ) : (
      <span className="muted">kein Kunde zugeordnet</span>
    );
  }
  if (gate.openEvents > 0) {
    return (
      <div className="dash-status" onClick={swallow}>
        <span className="muted">Bewertung ausstehend</span>
        {canReadTriage ? (
          <Link to="/triage">Zur Bewertung</Link>
        ) : null}
      </div>
    );
  }
  return (
    <span
      className="muted"
      title={
        gate.totalEvents > 0
          ? 'Alle Bewertungen dieser Instanz sind „Nicht betroffen“ oder „Behoben“.'
          : 'Für diese Instanz liegt noch keine Bewertung vor. Der KEV-Scan legt sie an.'
      }
    >
      {gate.totalEvents > 0 ? 'nicht anwendbar' : 'keine Bewertung'}
    </span>
  );
}

/** Share of affected rows: red for affected, green for the rest. */
function AffectedBar({ affected, total }: { affected: number; total: number }): ReactNode {
  if (total <= 0) return null;
  const share = Math.min(100, Math.max(0, (affected / total) * 100));
  return (
    <span className="dash-affected-bar" aria-hidden="true">
      <span style={{ width: `${share}%` }} />
    </span>
  );
}

function GroupStatus({ group }: { group: OverviewGroup }): ReactNode {
  if (group.affected === 0) return <Chip tone="good">keine KEV-Treffer</Chip>;
  const parts: ReactNode[] = [];
  if (group.ready > 0) {
    parts.push(
      <Chip key="ready" tone="warn" title="Bewertung „Betroffen“ liegt vor, Benachrichtigung fehlt">
        {group.ready} bereit
      </Chip>,
    );
  }
  if (group.awaiting > 0) {
    parts.push(
      <Chip key="awaiting" tone="idle" title="KEV-Treffer ohne Bewertung „Betroffen“">
        {group.awaiting} ohne Bewertung
      </Chip>,
    );
  }
  if (group.notified > 0) {
    parts.push(
      <Chip key="notified" tone="good">
        {group.notified} benachrichtigt
      </Chip>,
    );
  }
  return parts.length > 0 ? <ChipList>{parts}</ChipList> : <span className="muted">{DASH}</span>;
}

/* ------------------------------------------------------------------------- *
 * Instance table (inside an expanded group)
 * ------------------------------------------------------------------------- */

function instanceLabel(row: OverviewRow): string {
  const version = fmtVersion(row.produktversion.version_nr);
  return [
    row.produkt.seriennummer ? `S/N ${row.produkt.seriennummer}` : null,
    row.produktversion.version_nr ? (version === 'Demo' ? version : `V${version}`) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

interface InstanceTableProps extends SelectionProps {
  group: OverviewGroup;
  store: ExploitabilityStore;
}

function InstanceTable({
  group,
  store,
  gates,
  selected,
  canNotify,
  canReadTriage,
  onSelect,
  onNotifyOne,
}: InstanceTableProps): ReactNode {
  const columns: DataTableColumn<OverviewRow>[] = [
    {
      key: 'select',
      header: <span className="visually-hidden">Auswahl</span>,
      mobileLabel: 'Auswahl',
      width: '44px',
      render: (row) => {
        const gate = gates.get(row.produkt.id);
        const selectable = isSelectable(gate);
        return (
          <SelectBox
            state={selected.has(row.produkt.id) ? 'all' : 'none'}
            disabled={!canNotify || !selectable}
            label={`${orFallback(row.kunde.name, 'Kunde')} · ${orFallback(
              row.produkttyp.bezeichnung,
              'Produkt',
            )} ${instanceLabel(row)} auswählen`}
            title={
              !canNotify
                ? NO_PERMISSION_TITLE
                : selectable
                  ? 'Für die Benachrichtigung auswählen'
                  : gate?.kind === 'notified'
                    ? `Bereits benachrichtigt${
                        gate.versendetAm ? ` am ${fmtDate(gate.versendetAm)}` : ''
                      }. Die versendete Meldung steht im Audit-Log.`
                    : 'Erst nach einer Bewertung „Betroffen“ auswählbar'
            }
            onChange={(checked) => onSelect([row.produkt.id], checked)}
          />
        );
      },
    },
    {
      key: 'instanz',
      header: group.mode === 'kunde' ? 'Produkt' : 'Kunde',
      width: '32%',
      sortValue: (row) =>
        group.mode === 'kunde' ? row.produkttyp.bezeichnung : row.kunde.name,
      render: (row) => (
        <>
          <div className="kunde-name">
            {group.mode === 'kunde'
              ? orFallback(row.produkttyp.bezeichnung, 'Unbekannter Produkttyp')
              : orFallback(row.kunde.name, 'Unbekannter Kunde')}
            {group.mode === 'produkt' && row.kunde.ist_eu ? (
              <>
                {' '}
                <Chip tone="info" title="Kunde in der EU: CRA-Meldepflichten gelten">
                  EU
                </Chip>
              </>
            ) : null}
          </div>
          <div className="prod">{instanceLabel(row) || DASH}</div>
        </>
      ),
    },
    {
      key: 'komponenten',
      header: 'Betroffene Komponenten',
      width: '30%',
      sortValue: (row) => row.affected_count,
      render: (row) => <ComponentsCell row={row} />,
    },
    {
      key: 'cves',
      header: 'KEV-CVEs',
      width: '20%',
      sortValue: (row) => row.kev_cves.length,
      render: (row) => <CvesCell cves={row.kev_cves} max={3} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: '18%',
      render: (row) => (
        <InstanceStatus
          row={row}
          gate={gates.get(row.produkt.id)}
          canNotify={canNotify}
          canReadTriage={canReadTriage}
          onNotifyOne={onNotifyOne}
        />
      ),
    },
  ];

  return (
    <div className="dash-subwrap">
      <DataTable
        rows={group.rows}
        columns={columns}
        rowKey={(row) => row.produkt.id}
        renderDetail={(row) => <KomponentenDetail row={row} store={store} />}
        rowClassName={(row) => (selected.has(row.produkt.id) ? 'dash-selected' : undefined)}
        mobileBreakpoint={720}
        pageSize={pageSizeFor(group.rows.length)}
        paginationResetKey={group.key}
        resetPageOnRowsChange={false}
        caption={group.mode === 'kunde' ? 'Produkte des Kunden' : 'Auslieferungen des Produkts'}
      />
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Group table
 * ------------------------------------------------------------------------- */

export interface GroupTableProps extends SelectionProps {
  mode: GroupMode;
  groups: readonly OverviewGroup[];
  store: ExploitabilityStore;
  emptyText: ReactNode;
  paginationResetKey: string;
}

export function GroupTable({
  mode,
  groups,
  store,
  emptyText,
  paginationResetKey,
  ...selection
}: GroupTableProps): ReactNode {
  const { gates, selected, canNotify, onSelect } = selection;

  const columns: DataTableColumn<OverviewGroup>[] = [
    {
      key: 'select',
      header: <span className="visually-hidden">Auswahl</span>,
      mobileLabel: 'Auswahl',
      width: '44px',
      render: (group) => {
        const ids = bulkIds(group, gates);
        const state = groupSelectState(group, gates, selected);
        return (
          <SelectBox
            state={state}
            disabled={!canNotify || (ids.length === 0 && state === 'none')}
            label={`Alle bereiten Produkte von ${group.title} auswählen`}
            title={
              !canNotify
                ? NO_PERMISSION_TITLE
                : ids.length === 0
                  ? 'Keine Produkte bereit zur Benachrichtigung'
                  : `${countLabel(ids.length, 'bereites Produkt', 'bereite Produkte')} auswählen`
            }
            onChange={(checked) =>
              onSelect(checked ? ids : group.rows.map((row) => row.produkt.id), checked)
            }
          />
        );
      },
    },
    {
      key: 'name',
      header: mode === 'kunde' ? 'Kunde' : 'Produkt',
      width: '34%',
      sortValue: (group) => group.title,
      render: (group) => (
        <>
          <div className="kunde-name">
            {group.title}
            {group.euKunde ? (
              <>
                {' '}
                <Chip tone="info" title="Kunde in der EU: CRA-Meldepflichten gelten">
                  EU
                </Chip>
              </>
            ) : null}
          </div>
          <div className="prod">
            {mode === 'kunde'
              ? group.subtitle !== null
                ? <><Icon name="mail" size={12} /> {group.subtitle}</>
                : group.hasKunde
                  ? <><Icon name="warning" size={12} /> keine Kontakt-E-Mail</>
                  : 'Produktinstanz ohne Kunde'
              : [
                  group.subtitle,
                  `${countLabel(group.rows.length, 'Auslieferung', 'Auslieferungen')} bei ${countLabel(
                    group.counterparts,
                    'Kunde',
                    'Kunden',
                  )}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </div>
        </>
      ),
    },
    {
      key: 'betroffen',
      header: 'Betroffen',
      width: '18%',
      sortValue: (group) => group.affected,
      render: (group) => (
        <>
          <span className="count-pill">{group.affected}</span>{' '}
          <span className="muted">
            von {countLabel(group.rows.length, mode === 'kunde' ? 'Produkt' : 'Auslieferung', mode === 'kunde' ? 'Produkten' : 'Auslieferungen')}
          </span>
          <AffectedBar affected={group.affected} total={group.rows.length} />
        </>
      ),
    },
    {
      key: 'cves',
      header: 'KEV-CVEs',
      width: '22%',
      sortValue: (group) => group.cves.length,
      render: (group) => <CvesCell cves={group.cves} max={3} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: '26%',
      headerTitle: 'Benachrichtigung erst nach einer Bewertung „Betroffen“ in der Triage.',
      sortValue: (group) => group.ready,
      render: (group) => <GroupStatus group={group} />,
    },
  ];

  return (
    <DataTable
      rows={groups}
      columns={columns}
      rowKey={(group) => group.key}
      renderDetail={(group) => <InstanceTable group={group} store={store} {...selection} />}
      emptyText={emptyText}
      mobileBreakpoint={760}
      pageSize={pageSizeFor(groups.length)}
      paginationResetKey={paginationResetKey}
      resetPageOnRowsChange={false}
      caption={mode === 'kunde' ? 'Übersicht je Kunde' : 'Übersicht je Produkt'}
    />
  );
}
